import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { connect } from 'react-redux';
import { webCampaignBaseUrl } from '_apis';
import { eventTracker } from '_helpers';
import WWTK from './WWTK';
import Campaign from './Campaigns';
import Blogs from './Blogs';
import SpotlightMaterial from './SpotlightMaterial/SpotlightMaterial';
import CampaignIframe from '../Campaign/Campaign';
import RcForKids from './RcForKids/RcForKids';
import RecycleFundamentals from './RecycleFundamentals';
import RecycleRIGHTGame from './RecycleRIGHTGame';
import events from './events';
import { DownloadAppBanner } from '../../components';

const OpacityAnimation = keyframes`
  from {opacity: 0;}
  to {opacity: 1;}
`;

const Container = styled.div`
  animation: ${OpacityAnimation} 0.5s;
  padding: 10px;
  * {
    box-sizing: border-box;
  }
`;

const Education = ({
  wwtkItems,
  campaigns,
  blogs,
  spotlight_materials,
  rf_lessons,
  districtId,
  projectId,
  uuid,
  selectedLang,
  zones,
  storeDistricts,
  recycleRightData,
  hasKidsKit
}) => {
  const blogsRef = useRef();
  const SPRef = useRef();
  const RFRef = useRef();
  const campaignRef = useRef();

  const [ref, setRef] = useState();
  const [showCampaignIframe, setShowCampignIframe] = useState();
  const [windowsWidth, setWindowsWidth] = useState(document.getElementById('rcroot').getBoundingClientRect().width);
  const [displayedContent, setDisplayedContent] = useState();

  const { stage } = storeDistricts[districtId];

  useEffect(() => {
    const updateWindowDimensions = () => {
      setWindowsWidth(document.getElementById('rcroot').getBoundingClientRect().width);
    };
    window.addEventListener('resize', updateWindowDimensions);

    return () => window.removeEventListener('resize', updateWindowDimensions);
  }, []);

  useEffect(() => {
    if (!displayedContent && !showCampaignIframe
    ) {
      if (ref === 'blogsRef') {
        blogsRef.current.scrollIntoView();
      }
      if (ref === 'campaignRef') {
        campaignRef.current.scrollIntoView();
      }
      if (ref === 'RFRef') {
        RFRef.current.scrollIntoView();
      }
      if (ref === 'SPRef') {
        SPRef.current.scrollIntoView();
      }
    }
  }, [displayedContent, showCampaignIframe]);

  useEffect(() => {
    eventTracker(events.DZ_HOME_LAND, '');
  }, []);

  const campaignLink = `${webCampaignBaseUrl}/?projectId=${projectId}&districtId=${districtId}&uuid=${uuid}&lang=${selectedLang}&zones=${zones}&runson=webapp`;

  if (showCampaignIframe) {
    return (
      <CampaignIframe
        education
        setShowCampignIframe={setShowCampignIframe}
        src={campaignLink}
        setRef={setRef}
      />
    );
  }

  /**
   * Check 6 different Edge Cases for Recycle RIGHT game
   * If one of the edge cases were detetced in the data,
   * the Recycle RIGHT game will not appear in Discovery Zone page
   */
  const checkEdgeCaseRecycleRIGHT = () => {
    let hasNullMaterial = false;
    let redundantMatId = false;
    let hasNotRequiredKey = false;
    let notStandardlevelKey = false;

    /**
     * Edge Case 1:
     * If recycleRIGHT does not exist in Discovery Zone data
     */
    if (!recycleRightData) return false;

    /**
     * Edge Case 2:
     * If recycleRIGHT is empty
     */
    if (Object.keys(recycleRightData).length === 0) return false;

    /**
     * Edge Case 3:
     * If a level key name is not standard
     */
    Object.keys(recycleRightData).forEach((level, index) => {
      if (level !== `level_${index + 1}_materials`) {
        notStandardlevelKey = true;
      }
    });
    if (notStandardlevelKey) return false;

    /**
     * Edge Case 4:
     * If a level does not have any material
     */
    Object.keys(recycleRightData).forEach((level) => {
      if (recycleRightData[level].length === 0) hasNullMaterial = true;
    });
    if (hasNullMaterial) return false;

    /**
     * Edge Case 5:
     * If a mat_id was redundant in recycleRIGHT
     */
    const all_mat_id = [];
    Object.keys(recycleRightData).forEach((key) => {
      recycleRightData[key].forEach((element) => {
        if (all_mat_id.indexOf(element.mat_id) !== -1) {
          redundantMatId = true;
        } else {
          all_mat_id.push(element.mat_id);
        }
      });
    });
    if (redundantMatId) return false;

    /**
     * Edge Case 6:
     * If a material does not have all the required keys or one of the keys value is null
     */
    const materialKeys = ['disposal_body', 'disposal_header', 'is_recyclable', 'mat_id', 'mat_image', 'mat_nm', 'oidx'];
    Object.keys(recycleRightData).forEach((key) => {
      recycleRightData[key].forEach((material) => {
        materialKeys.forEach((requiredKey) => {
          if (!(requiredKey in material) || material[requiredKey] === null) {
            hasNotRequiredKey = true;
          }
        });
      });
    });
    if (hasNotRequiredKey) return false;

    return true;
  };

  return (
    <Container windowsWidth={windowsWidth}>
      {/* wwtk */}
      {!displayedContent
        && Object.keys(wwtkItems).length > 0
        && (
          <WWTK />
        )}

      {/* campaigns */}
      {(!displayedContent || displayedContent === 'campaign')
        && stage === '4'
        && Object.keys(campaigns).length > 0
        && (
          <div ref={campaignRef}>
            <Campaign
              setShowCampignIframe={setShowCampignIframe}
              hasWwtk={Object.keys(wwtkItems).length}
              windowsWidth={windowsWidth}
              setDisplayedContent={setDisplayedContent}
            />
          </div>
        )}

      {/* recycle fundamentals */}
      {(!displayedContent || displayedContent === 'rfLessons')
        && stage === '4'
        && rf_lessons
        && rf_lessons.length !== 0
        && Object.keys(rf_lessons).length !== 0
        && (
          <div ref={RFRef}>
            <RecycleFundamentals
              windowsWidth={windowsWidth}
              setDisplayedContent={setDisplayedContent}
              setRef={setRef}
            />
          </div>
        )}

      {/* Recycle RIGHT */}
      {(!displayedContent || displayedContent === 'recycleRIGHT')
        && stage === '4'
        && checkEdgeCaseRecycleRIGHT()
        && (
          <RecycleRIGHTGame
            windowsWidth={windowsWidth}
            setDisplayedContent={setDisplayedContent}
          />
        )}

      {/* spotlight materials */}
      {(!displayedContent || displayedContent === 'spotlightMaterial')
        && stage === '4'
        && spotlight_materials
        && spotlight_materials.length !== 0
        && Object.keys(spotlight_materials).length !== 0
        && (
          <div ref={SPRef}>
            <SpotlightMaterial
              windowsWidth={windowsWidth}
              setDisplayedContent={setDisplayedContent}
              setRef={setRef}
            />
          </div>
        )}

      {/* rc for kids */}
      {!displayedContent
        && stage === '4'
        && hasKidsKit
        && <RcForKids windowsWidth={windowsWidth} />}

      {/* blogs */}
      {(!displayedContent || displayedContent === 'blog')
        && blogs
        && blogs.length !== 0
        && (
          <div ref={blogsRef}>
            <Blogs
              windowsWidth={windowsWidth}
              setDisplayedContent={setDisplayedContent}
              setRef={setRef}
            />
          </div>
        )}

      {!displayedContent && <DownloadAppBanner />}
    </Container>
  );
};
export default connect(
  (store) => ({
    zones: store.zone.zoneStr,
    projectId: store.plugin.projectId,
    uuid: store.plugin.uuid,
    districtId: store.config.districtId,
    countryCode: store.config.countryCode,
    storeDistricts: store.plugin.storeDistricts,
    wwtkItems: store.education.wwtk_items,
    campaigns: store.education.campaigns,
    blogs: store.education.blogs,
    rf_lessons: store.education.rf_lessons,
    spotlight_materials: store.education.spotlight_materials,
    recycleRightData: store.education.recycle_right,
    hasKidsKit: store.education.has_kids_kit
  })
)(Education);
